import React, { useState } from 'react';
import { Button, Modal, Row, Col } from "react-bootstrap";
import { ActionHandlerFunction, AdminUser } from '@preveil-api';
import { Account, AdminErrorMessages, AdminSuccessMessages, Message, useAppDispatch, account_types, usePutPendingAccountTypeMutation } from "src/common";
import { AdminUIActionTypes } from "src/common/constants/action-types";
import { MessageAnchors } from 'src/common/constants/messages';
import { Loading } from "src/components";
import { adminActions, uiActions } from "src/store";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  show_modal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  invalid_users: Array<[AdminUser, string]>;
  valid_users: AdminUser[];
  handleAction: ActionHandlerFunction;
}

// Description: Modal to convert a bulk of users to an express account.
function BulkConvertToExpressModalComponent(props: AllProps) {
  const { current_account, show_modal, setShowModal, invalid_users, valid_users, handleAction } = props;
  const dispatch = useAppDispatch();
  const [is_loading, setIsLoading] = useState<boolean>(false);
  const [putPendingAccountType] = usePutPendingAccountTypeMutation();

  // Description: set the users' pending account type to express. Close the modal and refresh the page when successful.
  async function handleConvert() {
    setIsLoading(true);
    try {
      await putPendingAccountType({
      account_ids: Account.getAccountIdentifiers(current_account),
      body: {
        entity_id: current_account.org_info?.org_id || "",
        users: valid_users.map((user) => user.user_id),
          account_type: account_types.express,
        },
      }).unwrap();
      dispatch(adminActions.setSelected([]));
      setShowModal(false);
      handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest });
      dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_bulk_convert_to_express.replace(MessageAnchors.total_count, `${valid_users.length}`))));
    } catch (error) {
      dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_bulk_convert_to_express), error));
    } finally {
      setIsLoading(false);
    }
  }

  // Description: Close the modal and uncheck the selected users.
  function cancel() {
    setShowModal(false);
    dispatch(adminActions.setSelected([]));
  }
  
  return (
    <Modal size="lg" show={show_modal} onHide={cancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>Are you sure you want to convert these accounts?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
      {is_loading ? <Loading className="in-place" /> :
        <>
          <p>Express accounts <b>prevent</b> users from accessing Preveil mobile and desktop applications.</p>
          <p><b>{valid_users.length}</b> user{valid_users.length > 1 ? "s" : ""} will be converted to <b>Express</b> account{valid_users.length > 1 ? "s" : ""}.</p>
          {invalid_users.length > 0 && (
            <>
              <p>The following users cannot be converted as they are administrators, members of an approval group, or already are Express accounts.</p>
              <div className="table-display mt-3">
                <Row className="header-card-row">
                  <h5 className="m-0">{invalid_users.length} User{invalid_users.length > 1 ? "s" : ""} that will not be converted </h5>
                </Row>
                <div className="table-body users">
                  {_.map(invalid_users, (user, index: number) => (
                    <Row key={user[0].user_id || index}>
                      <Col xs={7}>
                        <p className="semi-bold">{user[0].display_name}</p>
                        <p>{user[0].user_id.toLowerCase()}</p>
                      </Col>
                      <Col xs={5}>
                        <p className="m-0">{user[1]}</p>
                      </Col>
                    </Row>
                  ))}
                </div>
              </div>
            </>
          )}
          <p>Would you like to continue?</p>
        </>
      }
      </Modal.Body>
      <Modal.Footer>
        {!is_loading &&
        <>
          <Button variant="primary" onClick={handleConvert}>Convert</Button>
          <Button variant="no-outline-primary" onClick={cancel}>Cancel</Button>
        </>
        }
      </Modal.Footer>
    </Modal>
  );
}

export default React.memo(BulkConvertToExpressModalComponent);
